import React from "react";
import { components } from "react-select";
import axiosResiInstance from "../http/axiosResi";
import clone from "../helper/clone";

export const LISTING_SOURCE_STATUSES = {
    active: "Active",
    archive: "Archive",
    pending: "Pending",
    // Special
    cancelled: "Canceled",
    closed: "Closed",
    coming_soon: "Coming Soon",
    delete: "Delete",
    expired: "Expired",
    hold: "Hold",
    incomplete: "Incomplete",
    withdrawn: "Withdrawn",
};

export const LISTING_ACTIVE_STATUS = "active";
export const LISTING_ARCHIVE_STATUS = "archive";
export const LISTING_DEACTIVATED_STATUS = "deactivated";
export const LISTING_HIDDEN_STATUS = "hidden";

export const LISTING_PURCHASE_CATEGORY = "PURCHASE";
export const LISTING_RENT_CATEGORY = "RENT";

export const TYPES = {
    AUCTION: "Auction",
    FORECLOSURE: "Foreclosure",
    OTHER: "For Sale",
    HUD_HOME: "HUD Home",
    LAND: "Land",
    SHORT_SALE: "Short Sale",
};

export const PROPERTY_TYPES = {
    COMMERCIAL: "Commercial",
    COMMON: "Common Interest",
    FARM: "Farm And Agriculture",
    LOTS: "Lots And Land",
    MULTIFAMILY: "MultiFamily",
    OTHER: "Other",
    RENTAL: "Rental",
    RESIDENTIAL: "Residential",
};

export const PROPERTY_SUB_TYPES = {
    AGRICULTURE: "Agriculture",
    APARTMENT: "Apartment",
    BOATSLIP: "Boatslip",
    BUSINESS: "Business",
    CABIN: "Cabin",
    CONDOMINIUM: "Condominium",
    DEEDED_PARKING: "Deeded Parking",
    DUPLEX: "Duplex",
    FARM: "Farm",
    HOTEL_MOTEL: "Hotel Motel",
    INDUSTRIAL: "Industrial",
    MANUFACTURED_HOME: "Manufactured Home",
    MANUFACTURED_ON_LAND: "Manufactured On Land",
    MIXED_USE: "Mixed Use",
    MOBILE_HOME: "Mobile Home",
    MULTI_FAMILY: "Multi Family",
    OFFICE: "Office",
    OTHER: "Other",
    OWN_YOUR_OWN: "Own Your Own",
    QUADRUPLEX: "Quadruplex",
    RANCH: "Ranch",
    RETAIL: "Retail",
    SINGLE_FAMILY_ATTACHED: "Single Family Attached",
    SINGLE_FAMILY_DETACHED: "Single Family Detached",
    SINGLE_FAMILY_RESIDENCE: "Single Family Residence",
    STOCK_COOPERATIVE: "Stock Cooperative",
    TIMESHARE: "Timeshare",
    TOWNHOUSE: "Townhouse",
    TRIPLEX: "Triplex",
    UNIMPROVED_LAND: "Unimproved Land",
    WAREHOUSE: "Warehouse",
};

export const REQUIRED_NUMERIC_FIELDS = [
    { label: "Bedrooms", name: "bedrooms", max: 9999 },
    { label: "Bathrooms", name: "bathrooms", max: 9999 },
    { label: "Living Area", name: "living_area", max: 9999999999 },
    { label: "Year Built", name: "year_built", max: new Date().getFullYear() },
];

export const FEATURE_BOOL_FIELDS = [
    { label: "Basement", name: "has_basement" },
    { label: "Dock", name: "has_dock" },
    { label: "Elevator", name: "has_elevator" },
    { label: "Fireplace", name: "has_fireplace" },
    { label: "Greenhouse", name: "has_greenhouse" },
    { label: "Hot Tub Spa", name: "has_hot_tub_spa" },
    { label: "New Construction", name: "is_new_construction" },
    { label: "Pool", name: "has_pool" },
    { label: "Sauna", name: "has_sauna" },
    { label: "Sports Court", name: "has_sports_court" },
    { label: "Waterfront", name: "is_waterfront" },
];

export const OPTIONAL_NUMERIC_FIELDS = [
    { label: "Full Bathrooms", name: "full_bathrooms", max: 999 },
    {
        label: "3/4 Bathrooms",
        name: "three_quarter_bathrooms",
        max: 999,
    },
    { label: "Half Bathrooms", name: "half_bathrooms", max: 999 },
    { label: "Quarter Bathrooms", name: "one_quarter_bathrooms", max: 999 },
    { label: "Lot Size (in sqft)", name: "lot_size", max: 9999999999 },
    { label: "Price Low", name: "price_low", max: 9999999999 },
    { label: "Room Count", name: "room_count", max: 9999 },
    { label: "Building Unit Count", name: "building_unit_count", max: 9999 },
    { label: "Condo Floor Number", name: "condo_floor_num", max: 9999 },
    { label: "Number of Buildings", name: "number_of_buildings", max: 9999 },
    { label: "Number of Floors", name: "num_floors", max: 9999 },
    {
        label: "Parking Spaces",
        name: "num_parking_spaces",
        max: 9999999999,
    },
    { label: "Elevation", name: "elevation", max: 99999 },
    {
        label: "Year Updated",
        name: "year_updated",
        max: new Date().getFullYear(),
    },
];

export const OPTIONAL_TEXT_FIELDS = [
    { label: "Location Directions", name: "location_directions", max: 4096 },
    { label: "Legal Description", name: "legal_description", max: 4096 },
];

export const OPTIONAL_STRING_FIELDS = [
    { label: "Subdivision Name", name: "subdivision_name", max: 255 },
    { label: "Franchise Affiliation", name: "franchise_affiliation", max: 255 },
    { label: "Geocode Options", name: "geocode_options", max: 255 },
    { label: "Parcel Number", name: "parcel_number", max: 255 },
    { label: "Virtual Tour URL", name: "virtual_tour_url", max: 255 },
];

export const OPTIONAL_MULTI_LIST_FIELDS = [
    {
        label: "Special Listing Conditions",
        name: "special_listing_conditions",
        options: [
            { value: "Auction", label: "Auction" },
            { value: "Bankruptcy Property", label: "Bankruptcy Property" },
            { value: "HUD Owned", label: "HUD Owned" },
            { value: "In Foreclosure", label: "In Foreclosure" },
            { value: "Notice Of Default", label: "Notice Of Default" },
            { value: "Probate Listing", label: "Probate Listing" },
            { value: "Real Estate Owned", label: "Real Estate Owned" },
            { value: "Short Sale", label: "Short Sale" },
            { value: "Third Party Approval", label: "Third Party Approval" },
        ],
    },
];

export const OPTIONAL_ARRAY_DATA = [
    {
        label: "Alternate Prices",
        name: "alternate_prices",
        type: "fields",
        fields: [
            {
                label: "Price",
                name: "AlternateListPrice",
                type: "price",
            },
            {
                label: "Price Low",
                name: "AlternateListPriceLow",
                type: "price",
            },
        ],
    },
    {
        label: "Expenses",
        name: "expenses",
        type: "fields",
        fields: [
            {
                label: "Category",
                name: "ExpenseCategory",
                type: "list",
                list: [
                    "",
                    "Application Fee",
                    "Building Maintenance Fee",
                    "Capital Improvements Fee",
                    "Common Area Maintenance Fee",
                    "Community Development District Fee",
                    "Community Master Home Owner Fee",
                    "Condo Coop Fee",
                    "CoOp Fee",
                    "First Months Rent",
                    "Home Owner Assessments Fee",
                    "Home Owner Transfer Fee",
                    "Initiation Fee",
                    "Key Deposit",
                    "Last Months Rent",
                    "Management Fee",
                    "Other",
                    "Pet Deposit",
                    "POA Fee",
                    "Recreation Fee",
                    "Reservation Fee",
                    "Security Deposit",
                    "Tennis Fee",
                ],
            },
            {
                label: "Value",
                name: "ExpenseValue",
                type: "price",
            },
            {
                label: "Frequency",
                name: "ExpenseFrequency",
                type: "list",
                list: [
                    "",
                    "Once",
                    "Daily",
                    // Weekly
                    "Weekly",
                    "Bi-Weekly",
                    "Semi-Weekly",
                    // Monthly
                    "Monthly",
                    "Bi-Monthly",
                    "Semi-Monthly",
                    // Annually
                    "Annually",
                    "Bi-Annually",
                    "Semi-Annually",
                    // Other
                    "Seasonally",
                    "Quarterly",
                    "Once",
                    "Other",
                ],
            },
        ],
    },
    {
        label: "Taxes",
        name: "taxes",
        type: "fields",
        fields: [
            {
                label: "Year",
                name: "TaxYear",
                type: "year",
            },
            {
                label: "Annual Amount",
                name: "TaxAnnualAmount",
                type: "price",
            },
            {
                label: "Description",
                name: "TaxDescription",
                type: "string",
            },
        ],
    },
    {
        label: "Open House Showings",
        name: "listing_open_houses",
        type: "fields",
        fields: [
            {
                label: "Start Time",
                name: "start_time",
                type: "date_range",
            },
            {
                label: "End Time",
                name: "end_time",
                type: "date_range",
            },
            {
                label: "Description",
                name: "description",
                type: "string",
            },
            {
                label: "Appointment Required?",
                name: "is_appointment_required",
                type: "bool",
            },
        ],
    },
];

export const FEATURE_ARRAY_NAME = "features";
export const FEATURE_ARRAY_DATA = [
    {
        label: "Accessibility",
        name: "accessibility_features",
        type: "multiple_checkboxes",
        options: [
            "Accessible Approach with Ramp",
            "Accessible Bedroom",
            "Accessible Central Living Area",
            "Accessible Closets",
            "Accessible Common Area",
            "Accessible Doors",
            "Accessible Electrical and Environmental Controls",
            "Accessible Elevator Installed",
            "Accessible Entrance",
            "Accessible for Hearing-Impairment",
            "Accessible Full Bath",
            "Accessible Hallway(s)",
            "Accessible Kitchen",
            "Accessible Kitchen Appliances",
            "Accessible Stairway",
            "Accessible Washer/Dryer",
            "Adaptable Bathroom Walls",
            "Adaptable For Elevator",
            "Ceiling Track",
            "Central Living Area",
            "Common Area",
            "Customized Wheelchair Accessible",
            "Electronic Environmental Controls",
            "Enhanced Accessible",
            "Exterior Wheelchair Lift",
            "Grip-Accessible Features",
            "Reinforced Floors",
            "Safe Emergency Egress from Home",
            "Smart Technology",
            "Stair Lift",
            "Standby Generator",
            "Therapeutic Whirlpool",
            "Visitable",
            "Visitor Bathroom",
            "Walker-Accessible Stairs",
        ],
    },
    {
        label: "Appliances",
        name: "appliances",
        type: "multiple_checkboxes",
        options: [
            "Barbeque or Grill",
            "Coffee System",
            "Coffee System – Rough in",
            "Cooktop",
            "Cooktop – Electric",
            "Cooktop – Electric 2 burner",
            "Cooktop – Electric 6 burner",
            "Cooktop – Gas",
            "Cooktop – Gas 2 burner",
            "Cooktop – Gas 5 burner",
            "Cooktop – Gas 6 burner",
            "Cooktop – Gas Custom",
            "Cooktop – Induction",
            "Cooktop – Induction 2 burner",
            "Cooktop – Induction 6 burner",
            "Dishwasher",
            "Dishwasher – Drawer",
            "Dishwasher – Two or more",
            "Dryer",
            "Dryer – Dual fuel",
            "Dryer – Electric 110V",
            "Dryer – Electric 220V",
            "Dryer – Gas",
            "Dryer – Gas rough in",
            "Freezer",
            "Freezer – Compact",
            "Freezer – Upright",
            "Garbage Disposer",
            "Ice Maker",
            "Microwave",
            "None",
            "Other",
            "Oven",
            "Oven – Convection",
            "Oven – Double",
            "Oven – Double Electric",
            "Oven – Double Gas",
            "Oven – Gas",
            "Oven – Gas 3 wide",
            "Oven – Self-Cleaning",
            "Oven – Steam",
            "Oven – Twin",
            "Oven – Twin Electric",
            "Oven – Twin Gas",
            "Oven – Twin Gas 3 wide",
            "Oven – Twin Mixed",
            "Range",
            "Range – Built In",
            "Range – Dual",
            "Range – Dual 10 burner",
            "Range – Dual 6 burner",
            "Range – Dual 8 burner",
            "Range – Electric",
            "Range – Gas",
            "Range – Gas 10 burner",
            "Range – Gas 6 burner",
            "Range – Gas 8 burner",
            "Range – Induction",
            "Range – Other",
            "Rangetop – Electric",
            "Rangetop – Electric 2 burner",
            "Rangetop – Electric 6 burner",
            "Rangetop – Gas",
            "Rangetop – Gas 10 burner",
            "Rangetop – Gas 2 burner",
            "Rangetop – Gas 4 burner compact",
            "Rangetop – Gas 6 burner",
            "Rangetop – Gas 8 burner",
            "Rangetop – Gas Custom",
            "Rangetop – Induction",
            "Rangetop – Induction 2 burner",
            "Rangetop – Induction 6 burner",
            "Refrigerator",
            "Refrigerator – Bar",
            "Refrigerator – Built-in",
            "Refrigerator – Built-in With Plumbing",
            "Refrigerator – Drawer",
            "Refrigerator – Side by Side",
            "Refrigerator – Undercounter",
            "Refrigerator – Wine Storage",
            "Refrigerator – With Plumbing",
            "Trash Compactor",
            "Vacuum System",
            "Vacuum System – Rough in",
            "Vent Hood",
            "Vent Hood 10 burner",
            "Vent Hood 6 burner",
            "Vent Hood 8 burner",
            "Warming Drawer",
            "Washer",
            "Washer – Front load",
            "Washer – Steamer",
            "Washer – Top load",
            "Washer/Dryer Combo",
            "Washer/Dryer Stack",
            "Water – Filter",
            "Water – Instant Hot",
            "Water – Purifier",
            "Water – Softener",
        ],
    },
    {
        label: "Architectural Style",
        name: "architectural_style",
        type: "multiple_checkboxes",
        options: [
            "A Frame",
            "Art Deco",
            "Bungalow",
            "Cape Cod",
            "Colonial",
            "Contemporary",
            "Conventional",
            "Cottage",
            "Craftsman",
            "Creole",
            "Dome",
            "Dutch Colonial",
            "English",
            "Federal",
            "French",
            "French Provincial",
            "Georgian",
            "Gothic Revival",
            "Greek Revival",
            "High Rise",
            "Historical",
            "International",
            "Italianate",
            "Loft",
            "Mansion",
            "Mediterranean",
            "Modern",
            "Monterey",
            "Mountain",
            "National",
            "Neoclassical",
            "New Traditional",
            "Other",
            "Prairie",
            "Pueblo",
            "Queen Anne",
            "Rambler",
            "Ranch",
            "Regency",
            "Rustic",
            "Saltbox",
            "Santa Fe",
            "Second Empire",
            "Shed",
            "Shingle",
            "Shotgun",
            "Spanish",
            "Spanish Eclectic",
            "Split Level",
            "Stick",
            "Tudor",
            "Victorian",
        ],
    },
    {
        label: "Construction Materials",
        name: "construction_materials",
        type: "multiple_checkboxes",
        options: [
            "Adobe",
            "Aluminum Siding",
            "Asbestos",
            "Asphalt",
            "Attic Crawl Hatchways Insulated",
            "Batts Insulation",
            "Block",
            "Blown In Insulation",
            "Board & Batten Siding",
            "Brick",
            "Brick Veneer",
            "Cedar",
            "Cement Siding",
            "Clapboard",
            "Concrete",
            "Ducts Professionally Air-Sealed",
            "Exterior Duct-Work is Insulated",
            "Fiber Cement",
            "Fiberglass Siding",
            "Foam Insulation",
            "Frame",
            "Glass",
            "Hardiplank Type",
            "ICAT Recessed",
            "ICAT Recessed Lighting",
            "ICFs (Insulated Concrete Forms)",
            "Lap Siding",
            "Log",
            "Log Siding",
            "Low VOC Insulation",
            "Masonite",
            "Metal Siding",
            "Natural Building",
            "Other",
            "Plaster",
            "Radiant Barrier",
            "Rammed Earth",
            "Recycled Bio Based Insulation",
            "Redwood Siding",
            "See Remarks",
            "Shake Siding",
            "Shingle Siding",
            "Slump Block",
            "Spray Foam Insulation",
            "Steel Siding",
            "Stone",
            "Stone Veneer",
            "Straw",
            "Stucco",
            "Synthetic Stucco",
            "Unknown",
            "Vertical Siding",
            "Vinyl Siding",
            "Wood Siding",
        ],
    },
    {
        label: "Cooling",
        name: "cooling",
        type: "multiple_checkboxes",
        options: [
            "Attic Fan",
            "Ceiling Fan(s)",
            "Central A/C",
            "Central Evaporative",
            "Central Fan",
            "Chilled Water",
            "Dehumidifiers",
            "Desiccant Cooler",
            "Evaporative",
            "Heat Pumps",
            "None",
            "Other",
            "Partial",
            "Radiant Floor",
            "Radiant Floor Ground Loop",
            "Refrigerator/Evaporative",
            "Solar A/C-Active",
            "Solar A/C-Passive",
            "Unknown",
            "Wall Unit(s) A/C",
            "Wall Unit(s) Evaporative",
            "Window Unit(s) A/C",
            "Window Unit(s) Evaporative",
            "Zoned A/C",
        ],
    },
    {
        label: "Exterior",
        name: "exterior_features",
        type: "multiple_checkboxes",
        options: [
            "Awning",
            "Balcony",
            "Barbecue",
            "Basketball Court",
            "Boat Slip",
            "Built-in Barbecue",
            "Courtyard",
            "Dock",
            "Dog Run",
            "Electric Grill",
            "Fire Pit",
            "Garden",
            "Gas Grill",
            "Gray Water System",
            "Kennel",
            "Lighting",
            "Misting System",
            "None",
            "Other",
            "Outdoor Grill",
            "Outdoor Kitchen",
            "Outdoor Shower",
            "Permeable Paving",
            "Playground",
            "Private Entrance",
            "Private Yard",
            "Rain Barrel/Cistern(s)",
            "Rain Gutters",
            "RV Hookup",
            "Storage",
            "Tennis Court(s)",
            "Uncovered Courtyard",
        ],
    },
    {
        label: "Fencing",
        name: "fencing",
        type: "multiple_checkboxes",
        options: [
            "Back Yard",
            "Barbed Wire",
            "Block",
            "Brick",
            "Chain Link",
            "Cross Fenced",
            "Electric",
            "Fenced",
            "Front Yard",
            "Full",
            "Gate",
            "Invisible",
            "Masonry",
            "None",
            "Other",
            "Partial",
            "Partial Cross",
            "Perimeter",
            "Pipe",
            "Privacy",
            "Security",
            "See Remarks",
            "Slump Stone",
            "Split Rail",
            "Stone",
            "Vinyl",
            "Wire",
            "Wood",
            "Wrought Iron",
        ],
    },
    {
        label: "Flooring",
        name: "flooring",
        type: "multiple_checkboxes",
        options: [
            "Bamboo",
            "Brick",
            "Carpet",
            "Carpet – Full",
            "Carpet – Partial",
            "Concrete",
            "Concrete – Bare",
            "Concrete – Painted",
            "Cork",
            "Drainage",
            "Engineered Wood",
            "Glass",
            "Granite",
            "Hardwood",
            "Laminate",
            "Linoleum",
            "Load Restriction",
            "Marble",
            "None",
            "Other",
            "Parquet Wood",
            "Rough-in",
            "Slate",
            "Soft Wood",
            "Solid Wood",
            "Specialty",
            "Specialty Concrete",
            "Tile",
            "Tile – Ceramic",
            "Tile – Porcelain",
            "Tile – Stone",
            "Tile or Stone",
            "Unknown",
            "Vinyl",
            "Wood",
        ],
    },
    {
        label: "Heating",
        name: "heating",
        type: "multiple_checkboxes",
        options: [
            "Baseboard",
            "Central Furnace",
            "Electric Air Filter",
            "Fireplace",
            "Fireplace – Insert",
            "Floor Furnace",
            "Floor Wall",
            "Forced Air",
            "Geothermal",
            "Gravity Air",
            "Gravity Hot Water",
            "Heat Pump",
            "Hot Water",
            "Hot Water Radiant Floor",
            "Humidifier",
            "None",
            "Other",
            "Pellet Stove",
            "Radiant",
            "Radiant Ceiling",
            "Radiant Floor",
            "Radiator",
            "S-W Changeover",
            "Solar Active",
            "Solar Active and Passive",
            "Solar Passive",
            "Space Heater",
            "Steam",
            "Stove",
            "Unknown",
            "Wall Unit",
            "Zoned",
        ],
    },
    {
        label: "Interior",
        name: "interior_features",
        type: "multiple_checkboxes",
        options: [
            "Bar",
            "Beamed Ceilings",
            "Bidet",
            "Bookcases",
            "Breakfast Bar",
            "Built-in Features",
            "Cathedral Ceiling(s)",
            "Cedar Closet(s)",
            "Ceiling Fan(s)",
            "Central Vacuum",
            "Chandelier",
            "Coffered Ceiling(s)",
            "Crown Molding",
            "Crown Moldings",
            "Double Vanity",
            "Dry Bar",
            "Dumbwaiter",
            "Eat-in Kitchen",
            "Elevator",
            "Entrance Foyer",
            "Granite Counters",
            "High Ceilings",
            "High Speed Internet",
            "His And Hers Closets",
            "In-Law Floorplan",
            "Kitchen Island",
            "Laminate Counters",
            "Low Flow Plumbing Fixtures",
            "Master Downstairs",
            "Natural Woodwork",
            "Open Floorplan",
            "Other",
            "Pantry",
            "Recessed Lighting",
            "Sauna",
            "See Remarks",
            "Smart Home",
            "Smart Thermostat",
            "Soaking Tub",
            "Solar Tube(s)",
            "Sound System",
            "Stone Counters",
            "Storage",
            "Tile Counters",
            "Track Lighting",
            "Tray Ceiling(s)",
            "Vaulted Ceiling(s)",
            "Walk-In Closet(s)",
            "WaterSense Fixture(s)",
            "Wet Bar",
            "Wired For Data",
            "Wired For Sound",
        ],
    },
    {
        label: "Other Structures",
        name: "other_structures",
        type: "multiple_checkboxes",
        options: [
            "Airplane Hangar",
            "Arena",
            "Barn(s)",
            "Boat House",
            "Cabana",
            "Cave(s)",
            "Corral(s)",
            "Covered Arena",
            "Garage(s)",
            "Gazebo",
            "Grain Storage",
            "Greenhouse",
            "Guest House",
            "Kennel/Dog Run",
            "Mobile Home",
            "None",
            "Other",
            "Outbuilding",
            "Outdoor Kitchen",
            "Packing Shed",
            "Pergola",
            "Pool House",
            "Poultry Coop",
            "Residence",
            "RV/Boat Storage",
            "Second Garage",
            "Second Residence",
            "See Remarks",
            "Shed(s)",
            "Stable(s)",
            "Storage",
            "Tennis Court(s)",
            "Workshop",
        ],
    },
    {
        label: "Parking",
        name: "parking_features",
        type: "multiple_checkboxes",
        options: [
            "Alley",
            "Assigned",
            "Boat",
            "Built-in",
            "Carport",
            "Commercial",
            "Covered",
            "Driveway",
            "Fee",
            "Fenced",
            "Garage",
            "Garage – Attached",
            "Garage – Detached",
            "Gated",
            "Golf Cart",
            "Guest",
            "Heated",
            "Leased",
            "Mechanics",
            "Meter",
            "Mixed",
            "None",
            "Off Alley",
            "Off Street",
            "Offsite",
            "On Street",
            "Open",
            "Other",
            "Oversized",
            "Owned",
            "Parking Lot",
            "Parking Structure",
            "Paved or Surfaced",
            "Pole",
            "Porte-Cochere",
            "Pull-through",
            "Ramp",
            "RV",
            "Secured",
            "Side Apron",
            "Side by Side",
            "Special Needs",
            "Stacked",
            "Tandem",
            "Tuck-Under",
            "Unassigned",
            "Underground/Basement",
            "Unimproved",
            "Unknown",
            "Valet",
            "Workshop",
            "Zoned Permit",
        ],
    },
    {
        label: "Patio and Porch",
        name: "patio_and_porch_features",
        type: "multiple_checkboxes",
        options: ["Patio", "Porch", "Deck"],
    },
    {
        label: "Property Condition",
        name: "property_condition",
        type: "multiple_checkboxes",
        options: [
            "Fixer",
            "New Construction",
            "Under Construction",
            "Updated/Remodeled",
        ],
    },
    {
        label: "Roof",
        name: "roof",
        type: "multiple_checkboxes",
        options: [
            "Aluminum",
            "Asbestos Shingle",
            "Asphalt",
            "Bahama",
            "Barrel",
            "Bituthene",
            "Built-Up",
            "Composition",
            "Concrete",
            "Copper",
            "Elastomeric",
            "Fiberglass",
            "Flat",
            "Flat Tile",
            "Foam",
            "Green Roof",
            "Mansard",
            "Membrane",
            "Metal",
            "Mixed",
            "None",
            "Other",
            "Rolled Hot Mop",
            "Rubber",
            "See Remarks",
            "Shake",
            "Shingle",
            "Slate",
            "Spanish Tile",
            "Stone",
            "Synthetic",
            "Tar Gravel",
            "Tile",
            "Wood",
        ],
    },
    {
        label: "Rooms",
        name: "rooms",
        type: "multiple_checkboxes",
        options: [
            "Basement",
            "Bathroom 1",
            "Bathroom 2",
            "Bathroom 3",
            "Bathroom 4",
            "Bathroom 5",
            "Bedroom 1",
            "Bedroom 2",
            "Bedroom 3",
            "Bedroom 4",
            "Bedroom 5",
            "Bonus Room",
            "Den",
            "Dining Room",
            "Exercise Room",
            "Family Room",
            "Game Room",
            "Great Room",
            "Gym",
            "Kitchen",
            "Laundry",
            "Library",
            "Living Room",
            "Loft",
            "Master Bathroom",
            "Master Bedroom",
            "Media Room",
            "Office",
            "Sauna",
            "Utility Room",
            "Workshop",
        ],
    },
    {
        label: "Security",
        name: "security_features",
        type: "multiple_checkboxes",
        options: [
            "Security",
            "Security Alarm",
            "Security Lighting",
            "Security System",
        ],
    },
    {
        label: "Structure Type",
        name: "structure_type",
        type: "multiple_checkboxes",
        options: [
            "Cabin",
            "Dock",
            "Duplex",
            "Flex",
            "Hotel Motel",
            "House",
            "Industrial",
            "Manufactured House",
            "Mixed Use",
            "Multi Family",
            "None",
            "Office",
            "Quadruplex",
            "Retail",
            "Townhouse",
            "Triplex",
            "Warehouse",
        ],
    },
    {
        label: "Utilities",
        name: "utilities",
        type: "multiple_checkboxes",
        options: [
            "Cable Available",
            "Cable Connected",
            "Cable Not Available",
            "Electricity Available",
            "Electricity Connected",
            "Electricity Not Available",
            "Natural Gas Available",
            "Natural Gas Connected",
            "Natural Gas Not Available",
            "None",
            "Other",
            "Phone Available",
            "Phone Connected",
            "Phone Not Available",
            "Propane",
            "See Remarks",
            "Sewer Available",
            "Sewer Connected",
            "Sewer Not Available",
            "Underground Utilities",
            "Water Available",
            "Water Connected",
            "Water Not Available",
        ],
    },
    {
        label: "Vegetation",
        name: "vegetation",
        type: "multiple_checkboxes",
        options: [
            "Brush",
            "Cleared",
            "Crops",
            "Grassed",
            "Heavily Wooded",
            "Natural State",
            "Other",
            "Partially Wooded",
            "See Remarks",
            "Wooded",
        ],
    },
    {
        label: "View",
        name: "view",
        type: "multiple_checkboxes",
        options: [
            "Airport",
            "Average",
            "Bluff",
            "Bridge",
            "Canyon",
            "City",
            "Desert",
            "Forest",
            "Golf Course",
            "Harbor",
            "Hills",
            "Lake",
            "Marina",
            "Mountains",
            "None",
            "Ocean",
            "Other",
            "Panorama",
            "Park",
            "Ravine",
            "River",
            "Territorial",
            "Unknown",
            "Valley",
            "Vista",
            "Water",
        ],
    },
    {
        label: "Waterfront",
        name: "waterfront_features",
        type: "multiple_checkboxes",
        options: [
            "Beach Access",
            "Beach Front",
            "Canal Access",
            "Creek",
            "Lagoon",
            "Lake",
            "Lake Front",
            "Lake Privileges",
            "Navigable Water",
            "Ocean Access",
            "Ocean Front",
            "Pond",
            "River Access",
            "River Front",
            "Seawall",
            "Stream",
            "Waterfront",
        ],
    },
    {
        label: "Window",
        name: "window_features",
        type: "multiple_checkboxes",
        options: [
            "Aluminum Frames",
            "Bay Window(s)",
            "Blinds",
            "Display",
            "Display Window(s)",
            "Double Pane Windows",
            "Drapes",
            "ENERGY STAR Qualified Windows",
            "Garden Window(s)",
            "Insulated Windows",
            "Low Emissivity Windows",
            "Plantation Shutters",
            "Screens",
            "Shutters",
            "Skylight(s)",
            "Solar Screens",
            "Storm Window(s)",
            "Tinted Windows",
            "Triple Pane Windows",
            "Window Coverings",
            "Window Treatments",
            "Wood Frames",
        ],
    },
];

export const DropdownIndicator = (props) => {
    return (
        components.DropdownIndicator && (
            <components.DropdownIndicator {...props}>
                <div />
            </components.DropdownIndicator>
        )
    );
};

export const OptionIndicator = (props) => {
    return (
        <div>
            <components.Option {...props}>
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                />{" "}
                <label>{props.label}</label>
            </components.Option>
        </div>
    );
};

export const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        border: "1px solid #d9d9d9",
        boxShadow: "none",
        backgroundColor: "#fff",
        borderRadius: 0,
        height: 44,
    }),
    valueContainer: (provided) => ({
        ...provided,
        paddingLeft: 17,
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: 14,
        color: "#303030",
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: 14,
        color: "#444",
        fontWeight: "bold",
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
    }),
    dropdownIndicator: () => ({
        marginTop: "-8px",
        marginRight: 18,
        boxSizing: "border-box",
        height: "13px",
        width: "13px",
        borderStyle: "solid",
        borderColor: "#2c2c2c",
        borderWidth: "0px 1px 1px 0px",
        transform: "rotate(45deg)",
        transition: "border-width 150ms ease-in-out",
    }),
    menu: () => ({
        top: "100%",
        width: "100%",
        position: "absolute",
        zIndex: 1,
        background: "#fafafa",
    }),
    menuList: (provided) => ({
        ...provided,
        paddingTop: 0,
        paddingBottom: 0,
    }),
    option: (provided, state) => ({
        ...provided,
        padding: 12,
        fontSize: 14,
        color: "#444",
        backgroundColor: "none",
        "&:hover": {
            backgroundColor: "#32afe4",
            color: "#fff",
        },
    }),
};

export const selectMultiStyles = {
    control: (provided, state) => ({
        ...provided,
        border: "1px solid #d9d9d9",
        boxShadow: "none",
        backgroundColor: "#fff",
        borderRadius: 0,
        minHeight: 44,
    }),
    valueContainer: (provided) => ({
        ...provided,
        paddingLeft: 17,
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: 14,
        color: "#303030",
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: 14,
        color: "#444",
        fontWeight: "bold",
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
    }),
    menu: () => ({
        top: "100%",
        width: "100%",
        position: "absolute",
        zIndex: 1,
        background: "#fafafa",
    }),
    menuList: (provided) => ({
        ...provided,
        paddingTop: 0,
        paddingBottom: 0,
    }),
    option: (provided, state) => ({
        ...provided,
        padding: 12,
        fontSize: 14,
        color: "#444",
        backgroundColor: "none",
        "&:hover": {
            backgroundColor: "#32afe4",
            color: "#fff",
        },
    }),
};

export const getSelectFieldValue = (value, label) => {
    value = value ? value : "";
    label = label ? label : "";

    return {
        value,
        label,
    };
};

export const getSelectFieldValueAndLabel = (value) => {
    value = value ? value : "";

    return {
        value: value,
        label: value,
    };
};

export const getFormFieldName = (fieldName, fieldParamName, i) => {
    return fieldName + "[" + i + "][" + fieldParamName + "]";
};

export const getFormFieldValues = (fieldData) => {
    let values = [];
    let fieldRow;

    for (let row of fieldData.rows) {
        fieldRow = {};

        for (let field of row) {
            fieldRow[field.name] = field.value;
        }

        values.push(fieldRow);
    }

    return values;
};

export const getFormAttributesForArrayFields = (data) => {
    let listingArray, attributes, attributesFields, value, options;
    let allFields = [];

    OPTIONAL_ARRAY_DATA.forEach((arrayField) => {
        listingArray =
            data &&
            data[arrayField.name] &&
            Array.isArray(data[arrayField.name]) &&
            data[arrayField.name].length > 0
                ? data[arrayField.name]
                : null;

        attributes = {
            id: arrayField.name,
            label: arrayField.label,
            rows: [],
            deleted_rows: [],
        };

        if (listingArray) {
            listingArray.forEach((listingArrayField, i) => {
                attributesFields = [];

                // check if listing data has every field
                arrayField.fields.forEach((field) => {
                    options = [];

                    value =
                        listingArrayField[field.name] === undefined ||
                        listingArrayField[field.name] === null ||
                        listingArrayField[field.name] === ""
                            ? ""
                            : listingArrayField[field.name];

                    if (field.type === "list") {
                        options = getSelectOptions(field.list, false);
                        value = field.list.includes(value) ? value : "";
                    } else if (field.type === "date_range") {
                        if (value) {
                            // UTC date
                            value = new Date(Date.parse(value));

                            const dateUTC = Date.UTC(
                                value.getUTCFullYear(),
                                value.getUTCMonth(),
                                value.getUTCDate(),
                                value.getUTCHours(),
                                value.getUTCMinutes(),
                                value.getUTCSeconds()
                            );

                            value = new Date(dateUTC);
                        }
                    }

                    attributesFields.push({
                        type: field.type,
                        name: field.name,
                        label: field.label,
                        value,
                        options,
                        form_name: getFormFieldName(
                            arrayField.name,
                            field.name,
                            i
                        ),
                    });
                });

                attributes.rows.push(attributesFields);
            });
        } else {
            attributesFields = [];

            arrayField.fields.forEach((field) => {
                options =
                    field.type === "list"
                        ? getSelectOptions(field.list, false)
                        : [];

                attributesFields.push({
                    type: field.type,
                    name: field.name,
                    label: field.label,
                    value: "",
                    options,
                    form_name:
                        arrayField.name + "[" + 0 + "][" + field.name + "]",
                });
            });

            attributes.rows.push(attributesFields);
        }

        allFields.push(attributes);
    });

    return allFields;
};

export const getFormAttributesForArrayCheckboxes = (data) => {
    let checked,
        options,
        allFields = [];

    FEATURE_ARRAY_DATA.forEach((field, i) => {
        options = [];
        checked = [];

        field.options.forEach((option) => {
            options.push({ value: option, label: option });
        });

        if (
            data &&
            data[FEATURE_ARRAY_NAME] &&
            data[FEATURE_ARRAY_NAME][field.name] &&
            Array.isArray(data[FEATURE_ARRAY_NAME][field.name]) &&
            data[FEATURE_ARRAY_NAME][field.name].length > 0
        ) {
            data[FEATURE_ARRAY_NAME][field.name].forEach((c) => {
                checked.push({ value: c, label: c });

                if (!field.options.includes(c)) {
                    options.push({ value: c, label: c });
                }
            });

            options.sort((a, b) => a.label.localeCompare(b.label));
        }

        allFields.push({
            name: field.name,
            label: field.label,
            options,
            checked,
        });
    });

    return allFields;
};

export const addFormAttributesForArrayFields = (fieldData) => {
    let rowFields = clone(fieldData.rows[0]);

    // new row values
    let newRow = [];

    rowFields.forEach((field) => {
        field["value"] = "";

        field["form_name"] = getFormFieldName(
            fieldData.id,
            field.name,
            fieldData.rows.length
        );

        newRow.push(field);
    });

    fieldData.rows.push(newRow);

    const newRowValues = getFormFieldValues(fieldData);

    return {
        fieldData,
        newRowValues: newRowValues[newRowValues.length - 1],
    };
};

export const deleteFormAttributesForArrayFields = (fieldData, keyRow) => {
    let rowFields = clone(fieldData.rows[keyRow]);

    // delete row values
    for (let i = 0; i < rowFields.length; i++) {
        rowFields[i]["value"] = "";
    }

    fieldData.rows[keyRow] = rowFields;
    fieldData.deleted_rows.push(keyRow);

    const newRowValues = getFormFieldValues(fieldData);

    return {
        fieldData,
        deletedRowValues: newRowValues[keyRow],
    };
};

export const categoryOptions = () => {
    return [
        {
            value: LISTING_PURCHASE_CATEGORY,
            label: "Purchase",
        },
        {
            value: LISTING_RENT_CATEGORY,
            label: "Rent",
        },
    ];
};

export const typeOptions = Object.entries(TYPES).map((item) => {
    return {
        value: item[0],
        label: item[1],
    };
});

export const checkAvailabilityProperty = (address) => {
    return axiosResiInstance.get("/check-availability-property", {
        params: {
            address,
        },
    });
};

export const fetchListing = (listingId) => {
    return axiosResiInstance(`/listings/${listingId}`);
};

export const createListing = (address, data) => {
    data["address"] = address;

    return axiosResiInstance.post("/listing", data);
};

export const postListing = (listing, data) => {
    return axiosResiInstance.post(`/agent-listings/${listing.id}`, data);
};

export const putListing = (listing, data) => {
    return axiosResiInstance.put(`/agent-listings/${listing.id}`, data);
};

export const convertSquareFeetToAcres = (squareFeet) => {
    if (!squareFeet) {
        return 0;
    }

    return (squareFeet * 2.29568e-5).toFixed(2);
};

export const settedFeature = (featureValue) => {
    return featureValue !== undefined && featureValue !== null;
};

export const hasFeature = (featureValue) => {
    if (featureValue === undefined || featureValue === null || !featureValue) {
        return false;
    }

    return (
        featureValue === "1" ||
        featureValue === 1 ||
        featureValue === "true" ||
        featureValue === true
    );
};

export const getEstatedValue = (featureValue) => {
    if (featureValue === undefined || featureValue === null) {
        return "";
    }

    return featureValue;
};

export const getSelectOptions = (list, hasKey = true) => {
    return Object.entries(list).map((item) => {
        return {
            value: hasKey ? item[0] : item[1],
            label: item[1],
        };
    });
};
